import { Component } from '@theme/component';

const TIME_ZONE = 'Asia/Kolkata';
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MS_PER_DAY = 86400000;

/**
 * Delivery estimate, calculated in India time (IST) regardless of the shopper's clock.
 *
 * Reads its rules from data attributes rendered by blocks/vw-delivery-estimate.liquid and replaces
 * the server-rendered fallback text. Dates are handled as IST calendar days encoded as UTC midnights,
 * so day arithmetic never crosses a time zone boundary.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} message
 * @property {HTMLElement} [countdown]
 *
 * @extends {Component<Refs>}
 */
class VwDeliveryEstimate extends Component {
  requiredRefs = ['message'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    // Re-render each minute so the countdown and the cutoff switch stay accurate.
    this.#timer = window.setInterval(() => this.#render(), 60000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  #render() {
    const { dataset } = this;
    const dispatchDays = Number(dataset.dispatchDays) || 0;
    const transitMin = Number(dataset.transitMin) || 0;
    const transitMax = Math.max(Number(dataset.transitMax) || 0, transitMin);
    const cutoffHour = Number(dataset.cutoffHour);
    const skip = new Set((dataset.skipDays || '').split(',').filter(Boolean));

    // Guard against a configuration that would loop forever.
    if (skip.size >= 7) return;

    const now = istParts(new Date());
    const today = Date.UTC(now.year, now.month - 1, now.day);
    const isWorkingDay = (/** @type {number} */ day) => !skip.has(dayKey(day));
    const nextWorkingDay = (/** @type {number} */ day) => {
      let next = day + MS_PER_DAY;
      while (!isWorkingDay(next)) next += MS_PER_DAY;
      return next;
    };

    const beforeCutoff = isWorkingDay(today) && now.hour < cutoffHour;
    let dispatch = beforeCutoff ? today : nextWorkingDay(today);
    for (let i = 0; i < dispatchDays; i++) dispatch = nextWorkingDay(dispatch);

    const from = deliveryDay(dispatch + transitMin * MS_PER_DAY, skip);
    const to = deliveryDay(dispatch + transitMax * MS_PER_DAY, skip);

    const template = (beforeCutoff ? dataset.messageBefore : dataset.messageAfter) || '';
    this.refs.message.textContent = fill(template, {
      dispatch: String(dispatchDays),
      from: formatDay(from),
      to: formatDay(to),
    });

    const { countdown } = this.refs;
    if (!countdown) return;

    if (beforeCutoff) {
      const minutesLeft = cutoffHour * 60 - (now.hour * 60 + now.minute);
      const hours = Math.floor(minutesLeft / 60);
      const minutes = minutesLeft % 60;
      const time = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      countdown.textContent = fill(dataset.countdown || '', { time });
      countdown.hidden = false;
    } else {
      countdown.hidden = true;
    }
  }
}

/**
 * Couriers don't deliver on skipped days either, so a delivery that lands on one moves to the next day.
 * @param {number} day
 * @param {Set<string>} skip
 */
function deliveryDay(day, skip) {
  let result = day;
  while (skip.has(dayKey(result))) result += MS_PER_DAY;
  return result;
}

/** @param {number} day - UTC midnight for an IST calendar day */
function dayKey(day) {
  return DAY_KEYS[new Date(day).getUTCDay()] ?? '';
}

/**
 * Replaces {name} placeholders in a merchant template.
 * @param {string} template
 * @param {Record<string, string>} values
 */
function fill(template, values) {
  return template.replace(/\{(\w+)\}/g, (match, name) => values[name] ?? match);
}

/** @param {number} day - UTC midnight for an IST calendar day */
function formatDay(day) {
  return new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(day)
  );
}

/** @param {Date} date */
function istParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (/** @type {Intl.DateTimeFormatPartTypes} */ type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

if (!customElements.get('vw-delivery-estimate')) {
  customElements.define('vw-delivery-estimate', VwDeliveryEstimate);
}
